import { Tag as TagAntd } from 'antd';
import cn from 'classnames';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, type BadgeColor } from '../badge';
import { resolveClassNames } from '../utils';
import styles from './tag.module.css';

export type TagColor = PaletteTagColor | SemanticTagColor;

export type TagProperties = {
  badge?: boolean;
  bordered?: boolean;
  color?: TagColor;
  coloredContent?: boolean;
  format?: { upperCase?: boolean };
  noBackground?: boolean;
  noPadding?: boolean;
  size?: 'compact' | 'default' | 'medium';
} & Omit<ComponentProps<typeof TagAntd>, 'color' | 'size'>;

type PaletteTagColor = 'blue' | 'cyan' | 'green' | 'orange-dark' | 'orange-light' | 'pink' | 'purple';

type SemanticTagColor = 'caution' | 'critical' | 'error' | 'info' | 'neutral' | 'success' | 'warning';

type TagSize = NonNullable<TagProperties['size']>;

const tagToBadgeColor: Partial<Record<TagColor, BadgeColor>> = {
  caution: 'caution',
  critical: 'error',
  error: 'error',
  neutral: 'neutral',
  success: 'success',
  warning: 'warning',
};

const mapTagColorToBadgeColor = (color?: TagColor) => color && tagToBadgeColor[color];

const tagColorStyles: Record<TagColor, string | undefined> = {
  blue: styles['tag--color-blue'],
  caution: styles['tag--color-caution'],
  critical: styles['tag--color-critical'],
  cyan: styles['tag--color-cyan'],
  error: styles['tag--color-error'],
  green: styles['tag--color-green'],
  info: styles['tag--color-info'],
  neutral: styles['tag--color-neutral'],
  'orange-dark': styles['tag--color-orange-dark'],
  'orange-light': styles['tag--color-orange-light'],
  pink: styles['tag--color-pink'],
  purple: styles['tag--color-purple'],
  success: styles['tag--color-success'],
  warning: styles['tag--color-warning'],
};

const tagSizeStyles: Record<TagSize, string | undefined> = {
  compact: styles['tag--size-compact'],
  default: styles['tag--size-default'],
  medium: styles['tag--size-medium'],
};

const resolveTagSize = (size?: TagProperties['size']): TagSize => size ?? 'default';

type TagClassNameProperties = Pick<
  TagProperties,
  'bordered' | 'className' | 'color' | 'coloredContent' | 'format' | 'noBackground' | 'noPadding' | 'size'
>;

const getTagClassName = ({
  bordered,
  className,
  color,
  coloredContent,
  format,
  noBackground,
  noPadding,
  size,
}: TagClassNameProperties) =>
  cn(className, styles.tag, color && tagColorStyles[color], tagSizeStyles[resolveTagSize(size)], {
    [styles['tag--bordered'] ?? '']: bordered,
    [styles['tag--colored-content'] ?? '']: coloredContent,
    [styles['tag--no-background'] ?? '']: noBackground,
    [styles['tag--no-padding'] ?? '']: noPadding,
    [styles['tag--uppercase'] ?? '']: format?.upperCase,
  });

const Tag = ({
  badge,
  bordered,
  children,
  className,
  classNames,
  color = 'neutral',
  coloredContent,
  format,
  icon,
  noBackground,
  noPadding,
  size,
  ...rest
}: TagProperties) => {
  const { t } = useTranslation();
  const content = typeof children === 'string' ? t(children) : children;

  return (
    <TagAntd
      {...rest}
      classNames={resolveClassNames(classNames, (resolvedClassNames) => ({
        ...resolvedClassNames,
        content: cn(styles.tag__content, resolvedClassNames?.content),
        icon: cn(styles.tag__icon, resolvedClassNames?.icon),
        root: cn(
          getTagClassName({
            bordered,
            className,
            color,
            coloredContent,
            format,
            noBackground,
            noPadding,
            size,
          }),
          resolvedClassNames?.root,
        ),
      }))}
      icon={icon}
    >
      {badge ? (
        <Badge
          color={mapTagColorToBadgeColor(color)}
          text={<span className={styles['tag__badge-text']}>{content}</span>}
        />
      ) : (
        content
      )}
    </TagAntd>
  );
};

export default Tag;
